import React from "react";
import {
  Grid,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import { extractTickerAndWeight } from "../../component/Methods";
import { toast } from "react-toastify";

const SignalsControls = ({
  strategyList,
  strategyVal,
  setStrategyVal,
  strategyValObj,
  strategySelected,
  strategies,
  pasted,
  setPasted,
  setVerifyEnabled,
  setCopiedData,
  setHandleVerify,
  setSaveChangeHandler,
  setNumberOfInvalidObjects,
  setEmpty,
  setError,
  error,
  empty,
  numberOfInvalidObjects,
  setSaveChange,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const reset = () => {
    setVerifyEnabled(false);
    setCopiedData([]);
    setNumberOfInvalidObjects(0);
    setEmpty(false);
    setError("");
  };

  const pasteFromClipboard = async () => {
    try {
      const entries = await navigator.clipboard.readText();

      // Try JSON first (fastest path)
      try {
        const result = JSON.parse(entries);
        if (
          Array.isArray(result) &&
          result.every((obj) => typeof obj === "object")
        ) {
          setPasted(result);
          reset();
          return;
        }
      } catch (jsonParseError) {
        // Continue to text processing
      }

      // Process as text - use Web Worker for anything over 50 entries
      const lines = entries.split("\n").filter((entry) => entry.trim() !== "");

      if (lines.length > 50) {
        // Use Web Worker for anything over 50 entries
        await processWithWebWorker(lines);
      } else {
        // For small datasets, process immediately without loading states
        const processedResult = processLinesImmediately(lines);
        setPasted(processedResult);
        reset();
      }
    } catch (err) {
      console.error("Paste error:", err);
      setPasted([]);
      alert("Error: Cannot paste from clipboard");
    }
  };

  const processLinesImmediately = (lines) => {
    const results = [];
    for (let i = 0; i < lines.length; i++) {
      const extracted = extractTickerAndWeight(lines[i]);
      if (extracted) {
        results.push(extracted);
      }
    }
    return results;
  };

  const processWithWebWorker = (lines) => {
    return new Promise((resolve, reject) => {
      // Show loading state only for larger datasets
      if (lines.length > 100) {
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg(`Processing ${lines.length} entries...`));
      }

      // Try to create Web Worker with fallback
      let worker;
      try {
        worker = new Worker("/tickerProcessor.js");
      } catch (workerError) {
        console.warn(
          "Web Worker failed to load, falling back to main thread processing:",
          workerError
        );
        // Fallback to main thread processing
        const results = processLinesImmediately(lines);
        setPasted(results);
        reset();
        dispatch(updateLoading(false));
        resolve(results);
        return;
      }

      worker.onmessage = function (e) {
        const { type, progress, processed, total, results, error } = e.data;

        switch (type) {
          case "progress":
            if (lines.length > 100) {
              dispatch(
                updateLoadingMsg(`Processing ${total} entries... ${progress}%`)
              );
            }
            break;

          case "complete":
            setPasted(results);
            reset();
            if (lines.length > 100) {
              toast.success(`Processed ${results.length} entries`);
            }
            dispatch(updateLoading(false));
            worker.terminate();
            resolve(results);
            break;

          case "error":
            console.error("Web Worker error:", error);
            toast.error("Error processing clipboard data");
            dispatch(updateLoading(false));
            worker.terminate();
            reject(new Error(error));
            break;
        }
      };

      worker.onerror = function (error) {
        console.error("Web Worker error:", error);
      

        const results = processLinesImmediately(lines);
        setPasted(results);
        reset();
        dispatch(updateLoading(false));
        worker.terminate();
        resolve(results);
      };

      // Start processing with larger batch size for better performance
      worker.postMessage({
        lines: lines,
        batchSize: Math.max(1000, Math.floor(lines.length / 10)),
      });
    });
  };

  return (
    <Box>
      <Grid container spacing={3} alignItems="center">
        {/* Strategy Selection */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Autocomplete
              options={strategyList || []}
              value={strategyVal}
              autoSelect
              onChange={(e, newValue) => {
                setStrategyVal(newValue);

                // Clear pasted data when strategy changes
                setPasted([]);

                const strObj = strategies?.find((i) => {
                  return i?.name === newValue;
                });
                if (strObj && strObj?.strategyId) {
                  strategySelected(strObj?.strategyId);
                } else {
                  // Clear strategy object when no strategy is selected
                  strategySelected(null);
                  // Clear all related data when no strategy is selected
                  setCopiedData([]);
                  setNumberOfInvalidObjects(0);
                  setEmpty(false);
                  setError("");
                }
              }}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Strategy to Manage"
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              )}
            />
          </FormControl>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                                                   {/* Error Messages */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 2,
                  gap: 1,
                  flexWrap: "wrap",
                  maxWidth: 400,
                }}
              >
                {error && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 0.5,
                      backgroundColor: theme.palette.error.dark,
                      color: theme.palette.error.contrastText,
                      fontSize: "11px",
                      fontWeight: 500,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      border: `1px solid ${theme.palette.error.main}`,
                      maxWidth: 400,
                      wordWrap: "break-word",
                    }}
                  >
                    <ErrorIcon sx={{ fontSize: 14, color: theme.palette.error.contrastText }} />
                    {error}
                  </Box>
                )}
              </Box>

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={
                !strategyVal || Boolean(error) || numberOfInvalidObjects > 0
              }
              onClick={() => {
                setSaveChange(true);
              }}
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 500,
                backgroundColor: theme.palette.success.main,
                "&:hover": {
                  backgroundColor: theme.palette.success.dark,
                },
              }}
            >
              Update Signals
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SignalsControls;
